// ═══════════════════════════════════════════════════════════════════════════
// NOTICE TEMPLATE AUTOMATION ENGINE — SYNTAX HELP & DOCUMENTATION DATA MODEL
// ═══════════════════════════════════════════════════════════════════════════

export const SYNTAX_HELP_CATEGORIES = [
  // ── 1. Variables ────────────────────────────────────────────────────────
  {
    id: 'variables',
    name: 'Variables',
    iconType: 'code',
    description: 'Dynamic placeholders that pull values from your uploaded data source.',
    tip: 'The variable name inside << >> must match the column header in your data source.',
    keywords: [
      'variable', 'variables', 'dynamic variable', 'field', 'borrower',
      'loan number', 'loan amount', 'placeholder', 'excel', 'data'
    ],
    items: [
      {
        id: 'var-dynamic-field',
        title: 'Dynamic Variable',
        badge: 'VARIABLE',
        meaning: 'Replaces the placeholder with the actual value from the uploaded data file.',
        syntax: '<<Variable Name>>',
        example: 'Mr. <<Borrower Name>>, Loan Account: <<Loan Number>>',
        data: 'Borrower Name = Ramesh Kumar\nLoan Number = LA-88902',
        sampleOutput: {
          type: 'simple_output',
          title: 'GENERATED OUTPUT',
          rendered: 'Mr. Ramesh Kumar, Loan Account: LA-88902'
        },
        commonExamples: [
          { name: '<<Borrower Name>>', output: 'Ramesh Kumar' },
          { name: '<<Loan Number>>', output: 'LA-88902' },
          { name: '<<Loan Amount>>', output: 'Rs. 25,00,000/-' },
          { name: '<<Notice Date>>', output: '24-Sep-2026' }
        ],
        notes: 'The variable name must match the column header in your Excel or CSV data file.'
      }
    ]
  },

  // ── 2. Optional ─────────────────────────────────────────────────────────
  {
    id: 'optional',
    name: 'Optional',
    iconType: 'optional',
    description: 'Statements that automatically disappear when enclosed variables are empty or NA.',
    tip: '{ } represents an optional block. If the variable inside is empty or NA, the statement is omitted without leaving blank lines.',
    keywords: [
      'optional', 'optional statement', 'optional block', '{ }', 'curly braces',
      'na', 'empty', 'null', 'remove block'
    ],
    items: [
      {
        id: 'opt-statement',
        title: 'Optional Statement',
        badge: 'OPTIONAL',
        meaning: 'Automatically removes the statement if the variable inside is empty, null, or NA. Leaves no blank lines.',
        syntax: '{Text containing <<Variable>>}',
        example: '{The co-borrower is <<Co-Borrower Name>>.}',
        data: 'Case 1: Co-Borrower Name = Priya Sharma (Exists)\nCase 2: Co-Borrower Name = NA (Empty)',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'When variable exists',
          whenTrue: 'The co-borrower is Priya Sharma.',
          whenFalseLabel: 'When variable is NA / empty',
          whenFalse: '[Statement is completely removed without blank lines]'
        },
        notes: 'Wrap the sentence in { }. The parser automatically removes the statement and cleans up surrounding blank lines.'
      }
    ]
  },

  // ── 3. Conditions ───────────────────────────────────────────────────────
  {
    id: 'conditions',
    name: 'Conditions',
    iconType: 'branch',
    description: 'Display clauses based on logic rules, value comparisons, or variable presence.',
    tip: 'Condition syntax uses {Text [Condition]}. For multiple rules, use && (AND) and || (OR).',
    keywords: [
      'condition', 'conditions', 'conditional', 'rule', 'exists', '==', '&&', '||', 'and', 'or'
    ],
    items: [
      {
        id: 'cond-syntax-structure',
        title: 'Value Comparison Condition',
        badge: 'CONDITION',
        meaning: 'Displays text only when the comparison condition is satisfied. Format is {Text [Condition]}.',
        syntax: '{Text [<<Variable>> == Value]}',
        example: '{Loan status is overdue. [<<Status>> == Overdue]}',
        data: 'Case 1: Status = Overdue (True)\nCase 2: Status = Active (False)',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'Condition = TRUE',
          whenTrue: 'Loan status is overdue.',
          whenFalseLabel: 'Condition = FALSE',
          whenFalse: '[Statement is removed]'
        },
        notes: 'Place the condition rule in square brackets at the end: {Text [Condition]}. Text comparisons are case-insensitive.'
      },
      {
        id: 'cond-exists-operator',
        title: 'EXISTS Condition',
        badge: 'CONDITION',
        meaning: 'Displays text only when the specified variable contains data (not empty, null, or NA).',
        syntax: '{Text [<<Variable>>]}',
        example: '{Contact phone: <<Phone Number>> [<<Phone Number>>]}',
        data: 'Case 1: Phone Number = 9876543210 (Exists)\nCase 2: Phone Number = NA (Missing)',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'When variable exists',
          whenTrue: 'Contact phone: 9876543210',
          whenFalseLabel: 'When variable is missing',
          whenFalse: '[Statement is removed]'
        },
        notes: '[<<Variable>>] checks for non-empty value. It is equivalent to [<<Variable>> EXISTS].'
      },
      {
        id: 'cond-multi-and-or',
        title: 'Multiple Conditions (&&, ||)',
        badge: 'CONDITION',
        meaning: 'Combine conditions using && (AND) or || (OR). Do NOT use the words AND or OR.',
        syntax: '{Text [Condition1 && Condition2]}',
        example: '{Eligible for waiver. [<<Overdue>> == Yes && <<Waiver>> == Approved]}',
        data: 'Case 1: Overdue = Yes, Waiver = Approved (Both True)\nCase 2: Waiver = Rejected (False)',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'Both conditions TRUE',
          whenTrue: 'Eligible for waiver.',
          whenFalseLabel: 'Any condition FALSE',
          whenFalse: '[Statement is removed]'
        },
        notes: 'Always use && for AND, and || for OR. Never write the words "AND" or "OR".'
      }
    ]
  },

  // ── 4. Clear Space ──────────────────────────────────────────────────────
  {
    id: 'clear-space',
    name: 'Clear Space (Hide)',
    iconType: 'clear',
    description: 'Clean up surrounding spaces, commas, or punctuation marks when optional variables are empty.',
    tip: 'Wrap optional punctuation in @ ... @ to prevent double spaces or dangling commas.',
    keywords: [
      'clear space', 'hide', '@', 'space', 'comma', 'punctuation', 'whitespace'
    ],
    items: [
      {
        id: 'clear-space-rule',
        title: 'Clear Space Rule (@)',
        badge: 'CLEAR SPACE',
        meaning: 'Removes unnecessary blank spaces or commas when an optional variable is empty or NA.',
        syntax: 'Mr. <<First Name>>@ <<Middle Name>>@ <<Last Name>>',
        example: 'Mr. <<First Name>>@ <<Middle Name>>@ <<Last Name>>',
        data: 'First Name = Ramesh, Middle Name = (empty), Last Name = Kumar',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'When Middle Name exists',
          whenTrue: 'Mr. Ramesh Chandra Kumar',
          whenFalseLabel: 'When Middle Name is empty',
          whenFalse: 'Mr. Ramesh Kumar (clean single space, no double space)'
        },
        notes: 'Use the @ button on the editor toolbar to quickly wrap optional variables and spacing.'
      }
    ]
  },

  // ── 5. Dollar (Skip Row) ────────────────────────────────────────────────
  {
    id: 'Dollar',
    name: 'Skip Row ($)',
    iconType: 'Dollar',
    description: 'Dynamically remove entire table rows when all variables within that row are blank or NA.',
    tip: 'Wrap variables in $ ... $ inside table cells. If every $ variable in the row is empty, the entire row is omitted.',
    keywords: [
      'Dollar', '$', 'clear row', 'table row', 'remove row', 'empty row', 'blank row'
    ],
    items: [
      {
        id: 'Dollar-rule',
        title: 'Skip row($)',
        badge: 'DOLLAR',
        meaning: 'Removes the entire table row if all variables marked with $ in that row are empty or NA in the data source.',
        syntax: '| S.No | $<<Variable>>$ |',
        example: '| 2 | Loan Agreement | $<<Exhibit 2>>$ |',
        data: 'Case 1: Exhibit 2 = Copy of Agreement (Row kept)\nCase 2: Exhibit 2 = NA (Row removed)',
        sampleOutput: {
          type: 'conditional_dual',
          title: 'GENERATED OUTPUT',
          whenTrueLabel: 'When variable has data',
          whenTrue: '| 2 | Loan Agreement | Copy of Agreement |',
          whenFalseLabel: 'When variable is NA / empty',
          whenFalse: '[Entire row 2 is completely removed from the table]'
        },
        notes: 'Use the $ button on the editor toolbar when inside a table cell to mark the row for dynamic removal.'
      }
    ]
  },

  // ── 6. Bold & Underline ─────────────────────────────────────────────────
  {
    id: 'Bold-Underline',
    name: 'Bold & Underline',
    iconType: 'type',
    description: 'Format headings, emphasize statutory terms, and organize section titles.',
    tip: 'Use standard keyboard shortcuts Ctrl+B (Bold) and Ctrl+U (Underline) directly in the editor.',
    keywords: [
      'Bold', 'Underline', 'heading', 'h1', 'h2', 'h3', 'title', 'format'
    ],
    items: [
      {
        id: 'style-bold-underline',
        title: 'Bold & Underline',
        badge: 'BOLD & UNDERLINE',
        meaning: 'Emphasizes key terms, borrower names, or statutory notice headers.',
        syntax: '**bold text** | __underlined text__',
        example: '**<<Borrower Name>>** overdue: __Rs. <<Amount>>__',
        data: 'Borrower Name = Ramesh Kumar, Amount = 25,000/-',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px;"><p style="margin:0;"><strong>Ramesh Kumar</strong> overdue: <u>Rs. 25,000/-</u></p></div>'
        },
        notes: 'Bold (Ctrl+B) and Underline (Ctrl+U) can be applied to both text and <<Variable>> tags.'
      },
      {
        id: 'style-headings',
        title: 'Headings (H1, H2, H3)',
        badge: 'HEADING',
        meaning: 'Standardizes document hierarchy with preset legal alignments and font styles.',
        syntax: '# H1 Title\n## H2 Heading\n### H3 Subheading',
        example: '# FINAL DEMAND NOTICE\n## STATEMENT OF CLAIM',
        data: '(Document heading structure)',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; text-align: center;"><div style="font-size: 14px; margin-bottom: 4px;">FINAL DEMAND NOTICE</div><div style="font-size: 13px; font-weight: bold;">STATEMENT OF CLAIM</div></div>'
        },
        notes: 'H1 is Center; H2 is Center + Bold; H3 is Center + Bold + Underline.'
      }
    ]
  },

  // ── 7. Alignment ────────────────────────────────────────────────────────
  {
    id: 'alignment',
    name: 'Alignment',
    iconType: 'align',
    description: 'Position titles, recipient addresses, and signature sign-offs across the page.',
    tip: 'Prefix markdown lines with <=, ==, or => to automatically set alignment during import.',
    keywords: [
      'align', 'alignment', 'left', 'center', 'right', '<=', '==', '=>'
    ],
    items: [
      {
        id: 'align-presets',
        title: 'Text Alignment',
        badge: 'ALIGNMENT',
        meaning: 'Positions paragraphs to the left, center, or right of the document page.',
        syntax: '<= Left text\n== Center text ==\n=> Right text',
        example: '<= To: <<Borrower Name>>\n== DEMAND NOTICE ==\n=> Date: <<Notice Date>>',
        data: 'Borrower Name = Ramesh Kumar, Notice Date = 24-Sep-2026',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px; line-height: 1.5;"><div style="text-align: right; color: #475569;">Date: 24-Sep-2026</div><div style="text-align: center; font-weight: bold; margin: 8px 0;">DEMAND NOTICE</div><div style="text-align: left;">To: Ramesh Kumar</div></div>'
        },
        notes: 'Left (<=) for addresses; Center (==) for titles; Right (=>) for dates and signatures.'
      }
    ]
  },

  // ── 8. Tables & Table Conditions ─────────────────────────────────────────
  {
    id: 'tables',
    name: 'Tables',
    iconType: 'table',
    description: 'Structured tables with column alignments, entire row conditions, and cell-level conditions.',
    tip: 'Tables support column alignments (|<=|==|=>|), entire row conditions ({|...| [Cond]}), and cell-level conditions (|{<<Col>> [Cond]}|).',
    keywords: [
      'table', 'tables', 'grid', 'table condition', 'row condition', 'column condition',
      'col if', 'row if', 'column alignment', '<=', '==', '=>'
    ],
    items: [
      {
        id: 'table-structure',
        title: 'Table Structure & Column Alignment',
        badge: 'TABLE',
        meaning: 'Defines a table grid with column alignments: Left (<=), Center (==), and Right (=>).',
        syntax: '|==|<=|=>|\n| S.No | Description | Amount |\n| 1 | Principal Dues | Rs. <<Amount>> |',
        example: '|==|<=|=>|\n| S.No | Description | Amount |\n| 1 | Principal Dues | Rs. <<Amount>> |',
        data: 'Amount = 25,00,000/-',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<table style="width: 100%; border-collapse: collapse; font-family: serif; font-size: 12.5px; margin: 4px 0;"><thead style="background: #f1f5f9;"><tr><th style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: center;">S.No</th><th style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left;">Description</th><th style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: right;">Amount</th></tr></thead><tbody><tr><td style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: center;">1</td><td style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left;">Principal Dues</td><td style="border: 1px solid #cbd5e1; padding: 6px 10px; text-align: right;">Rs. 25,00,000/-</td></tr></tbody></table>'
        },
        notes: 'The alignment row (|==|<=|=>|) applies formatting across columns without rendering as text.'
      },
      {
        id: 'table-row-condition',
        title: 'Entire Row Condition (Row IF)',
        badge: 'ROW CONDITION',
        meaning: 'Applies a condition to the entire table row: {|...| [Condition]}. If true, the row appears. If false, the entire row is omitted cleanly without leaving blank lines or broken borders.',
        syntax: '{| S.No | <<Field 1>> | <<Field 2>> | [Condition] }',
        example: '{| 2 | <<Co-Borrower Name>> | <<Address>> | [<<Co-Borrower Name>>] }',
        data: 'Case 1: Co-Borrower Name = Priya Sharma (Row appears)\nCase 2: Co-Borrower Name = NA (Entire row removed)',
        sampleOutput: {
          type: 'table_condition_dual',
          title: 'GENERATED OUTPUT',
          whenTrueTitle: 'Condition = TRUE (Row Appears)',
          whenTrueHtml: '<table style="width:100%; border-collapse:collapse; font-family:serif; font-size:12px;"><thead style="background:#f1f5f9;"><tr><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">S.No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Name</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Address</th></tr></thead><tbody><tr><td style="border:1px solid #cbd5e1; padding:4px 8px;">1</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Ramesh Kumar</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Chennai</td></tr><tr><td style="border:1px solid #cbd5e1; padding:4px 8px;">2</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Priya Sharma</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Bangalore</td></tr></tbody></table>',
          whenFalseTitle: 'Condition = FALSE (Entire Row Removed)',
          whenFalseHtml: '<table style="width:100%; border-collapse:collapse; font-family:serif; font-size:12px;"><thead style="background:#f1f5f9;"><tr><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">S.No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Name</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Address</th></tr></thead><tbody><tr><td style="border:1px solid #cbd5e1; padding:4px 8px;">1</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Ramesh Kumar</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Chennai</td></tr></tbody></table><div style="font-size:11px; color:#059669; margin-top:4px;">✔ Row 2 is omitted completely without leaving blank lines.</div>'
        },
        notes: 'In the editor, conditional rows are highlighted with a light purple indicator and a condition badge. Use the "Row IF" button on the table toolbar.'
      },
      {
        id: 'table-col-condition',
        title: 'Particular Column Condition (Col IF)',
        badge: 'COL CONDITION',
        meaning: 'Applies a condition to a specific cell / column only, not the entire row. If true, the cell value is shown. If false, that cell remains empty while all other columns in the row stay intact.',
        syntax: '| S.No | <<Field 1>> | {<<Field 2>> [Condition]} | <<Field 3>> |',
        example: '| 3 | <<Loan Number>> | <<Amount>> | {<<Interest Rate>> [<<Loan Number>>]} | <<Tenure>> |',
        data: 'Case 1: Loan Number exists -> Interest Rate shown\nCase 2: Loan Number missing -> Interest Rate cell is empty, row remains',
        sampleOutput: {
          type: 'table_condition_dual',
          title: 'GENERATED OUTPUT',
          whenTrueTitle: 'Condition = TRUE (Cell Populated)',
          whenTrueHtml: '<table style="width:100%; border-collapse:collapse; font-family:serif; font-size:12px;"><thead style="background:#f1f5f9;"><tr><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Loan No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Amount</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Interest</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Tenure</th></tr></thead><tbody><tr><td style="border:1px solid #cbd5e1; padding:4px 8px;">3</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">LA-302</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Rs. 5,00,000</td><td style="border:1px solid #cbd5e1; padding:4px 8px; background:#eff6ff;">11.5%</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">24M</td></tr></tbody></table>',
          whenFalseTitle: 'Condition = FALSE (Cell Empty, Row Remains)',
          whenFalseHtml: '<table style="width:100%; border-collapse:collapse; font-family:serif; font-size:12px;"><thead style="background:#f1f5f9;"><tr><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Loan No</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Amount</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Interest</th><th style="border:1px solid #cbd5e1; padding:4px 8px; text-align:left;">Tenure</th></tr></thead><tbody><tr><td style="border:1px solid #cbd5e1; padding:4px 8px;">3</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">LA-302</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">Rs. 5,00,000</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">&nbsp;</td><td style="border:1px solid #cbd5e1; padding:4px 8px;">24M</td></tr></tbody></table><div style="font-size:11px; color:#6366f1; margin-top:4px;">✔ Only the Interest cell is empty; all other cells in the row remain intact.</div>'
        },
        notes: 'Wrap only the cell content in {<<Cell>> [Condition]}. Use the "Col IF" button on the table toolbar in the editor to apply directly.'
      }
    ]
  },

  // ── 9. Page Breaks & Divider Lines ───────────────────────────────────────
  {
    id: 'page-breaks',
    name: 'Breaks & Lines',
    iconType: 'file',
    description: 'Insert hard page breaks and section divider lines.',
    tip: '<page break> creates a new page in preview and Word (.docx) export. <line> inserts a full-width divider.',
    keywords: [
      'page break', 'page breaks', '<page break>', 'new page', 'line', '<line>', 'divider'
    ],
    items: [
      {
        id: 'break-page',
        title: 'Page Break (<page break>)',
        badge: 'PAGE BREAK',
        meaning: 'Forces all following content onto a brand new page. Preserved as a native page break in Word (.docx) export.',
        syntax: '<page break>',
        example: '... Notice Recitals ...\n\n<page break>\n\n# ANNEXURE - I',
        data: '(Moves Annexure onto next page)',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 12.5px; text-align: center;"><div style="color: #64748b; margin-bottom: 8px;">... End of Page 1 ...</div><div style="padding: 4px; border: 1px dashed #3b82f6; background: #eff6ff; color: #1d4ed8; font-weight: bold; font-size: 11px; margin: 8px 0;">✂ PAGE BREAK — Content moves to Page 2</div><div style="font-weight: bold; margin-top: 8px;">ANNEXURE - I (Page 2)</div></div>'
        },
        notes: 'In Word (.docx) export, <page break> generates native Word page break elements.'
      },
      {
        id: 'break-line',
        title: 'Horizontal Line (<line>)',
        badge: 'LINE',
        meaning: 'Inserts a clean, full-width horizontal divider line between document sections.',
        syntax: '<line>',
        example: '== PARTICULARS ==\n<line>\nTake notice that the dues remain unpaid...',
        data: '(Renders a divider line)',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px;"><div style="text-align: center; font-weight: bold; margin-bottom: 6px;">PARTICULARS</div><hr style="border: none; border-top: 1px solid #cbd5e1; margin: 8px 0;"><div style="color: #334155;">Take notice that the dues remain unpaid...</div></div>'
        },
        notes: 'Type <line> or select Horizontal Line from the toolbar to insert a section divider.'
      }
    ]
  },

  // ── 10. Signatures ──────────────────────────────────────────────────────
  {
    id: 'signatures',
    name: 'Signatures',
    iconType: 'pen',
    description: 'Closing sign-offs, digital signature directives, and physical signature blank boxes.',
    tip: 'Right-align closing lines using "=>" or the Align Right toolbar button for clean legal formatting.',
    keywords: [
      'signature', 'signatures', 'sign-off', 'yours faithfully', 'advocate',
      'whiteblank', 'whitebox', 'seal', 'closing'
    ],
    items: [
      {
        id: 'sig-directive',
        title: 'Signature Directive',
        badge: 'SIGNATURE',
        meaning: 'Embeds a digital signature image with specified width, height, and alignment.',
        syntax: '=> ! <width> <height> <filename.png>',
        example: '=> ! 1.0 1.0 Signature.png',
        data: 'Width = 1.0 in, Height = 1.0 in, File = Signature.png, Align = Right (=>)',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px; text-align: right;"><div style="color: #334155; margin-bottom: 4px;">Yours faithfully,</div><div style="display: inline-block; padding: 4px 12px; border: 1px dashed #cbd5e1; background: #f8fafc; color: #059669; font-weight: 600; font-size: 11px; margin: 4px 0;">✍️ [DIGITAL SIGNATURE]</div><div style="font-weight: bold; margin-top: 4px;">(Authorized Signatory)</div></div>'
        },
        notes: 'Format is ! <width> <height> <filename.png>. Wrap in { => ! 1.0 1.0 Signature.png [Condition] } for conditional signatures.'
      },
      {
        id: 'sig-white-box',
        title: 'White Blank Box',
        badge: 'WHITEBOX',
        meaning: 'Inserts an empty white box with specified dimensions for physical ink signatures or official company stamps.',
        syntax: '=> ! <width> <height> WhiteBlankImage.png',
        example: '=> ! 2.0 1.0 WhiteBlankImage.png',
        data: 'Width = 2.0 in, Height = 1.0 in, File = WhiteBlankImage.png',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px; text-align: right;"><div style="color: #334155; margin-bottom: 6px;">For State Bank,</div><div style="display: inline-block; width: 100px; height: 50px; background: #ffffff; border: 1px solid #cbd5e1; border-radius: 3px; margin: 4px 0;"></div><div style="font-weight: bold; margin-top: 4px;">(Branch Manager)</div></div>'
        },
        notes: 'Measured in inches or cm. Displays as an empty blank box in preview and Word export.'
      },
      {
        id: 'sig-standard',
        title: 'Closing Sign-off',
        badge: 'SIGNATURE',
        meaning: 'Standard legal closing sign-off aligned to the right side of the page.',
        syntax: '=> Yours faithfully,\n=> \n=> (<<Signatory Name>>)\n=> <<Designation>>',
        example: '=> Yours faithfully,\n=> \n=> (<<Advocate Name>>)\n=> Counsel for Claimant',
        data: 'Advocate Name = S. K. Venkatesh',
        sampleOutput: {
          type: 'styled_text',
          title: 'GENERATED OUTPUT',
          htmlContent: '<div style="font-family: serif; font-size: 13px; text-align: right;"><div style="margin-bottom: 10px;">Yours faithfully,</div><div style="font-weight: bold;">(S. K. VENKATESH)</div><div style="color: #64748b; font-size: 12px;">Counsel for Claimant</div></div>'
        },
        notes: 'Prefix each line with => to align to the right.'
      }
    ]
  }
];
